import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAnonymousAuth } from './useAnonymousAuth';
import { useRecoverParticipant } from './useRecoverParticipant';
import { RecoveryOverwriteConfirm } from './RecoveryOverwriteConfirm';
import { RecoveryRecoverModal } from './RecoveryRecoverModal';
import { clearRecoveryCode, loadRecoveryCode, persistRecoveryCode } from './recoveryCodeStorage';

/**
 * Sprint BJ Part 1 — QR ディープリンク / 永続コードからの自動復帰ページ。
 *
 * route: `/m/:urlToken/recovery/auto[?c=CODE]`
 *
 * 旧 `old/apps/user/src/pages/RecoveryAutoPage.tsx` を v2 に移植:
 *   - 旧 `setMeta('recoveryCode:{districtId}', code)` (IndexedDB) → v2 `loadRecoveryCode(districtId)`
 *     (localStorage)。docs/97 §4-1 L2: docs 上で「IndexedDB ではなく localStorage」と決めた
 *     ため、key prefix も `cw.recoveryCode.v1.` に統一 (useNickname 等 v2 既存 pattern と一致)。
 *   - 旧 `useAuth` (AuthContext) → v2 `useAnonymousAuth` (state machine)。loading 状態は
 *     status === 'loading' 判定 (旧 authLoading に相当)。
 *   - 旧 `httpsCallable(functions, 'recoverParticipant')` 直叩き → v2 `useRecoverParticipant` hook
 *     (resource-exhausted / not-found / invalid-argument を専用文言に正規化済、ID token refresh 自動)。
 *   - 失敗時の手動入力 fallback: 旧は `/m/{districtToken}/recovery/recover` route に navigate していたが、
 *     v2 では RecoveryRecoverModal をその場で展開 (route 削減、modal 7 件目 consumer pattern 維持)。
 *   - 12 秒 timeout で「ブラウザ保存設定の確認」案内 (旧 L23-35 と同等)。
 *   - 復帰成功時は `/m/:urlToken/map` (旧の `/app/map` に相当する v2 主画面) に navigate replace。
 *
 * urlToken / c (query) どちらか欠けても自動復帰は試みる:
 *   - urlToken 欠如: error 表示 (district 不明では recoverParticipant が district 結びを取れない)
 *   - c 欠如: localStorage 保存 code を読み、それも無ければ手動入力 modal を即開く
 *   - urlToken + code 揃い: 自動で call → 成功 → localStorage に code を再保存して navigate
 *
 * R-70 P6 (2026-09-03): この端末に**別人の**参加情報があるときは自動では置き換えず、確認画面
 * (`RecoveryOverwriteConfirm`) を出して了承後に再送する。他人の端末で QR を読んだ / 他人の QR を
 * 誤って読んだときに、端末所有者の参加情報が黙って消える事故を防ぐ。
 */

type Step =
  | 'auth-pending'
  | 'recovering'
  | 'needs-confirm'
  | 'success'
  | 'error-no-code'
  | 'error-not-found'
  | 'error-timeout'
  | 'error-auth'
  | 'error-admin';

const STEP_LABEL: Record<Step, string> = {
  'auth-pending': '認証中…',
  recovering: '参加コードを確認中…',
  'needs-confirm': '確認が必要です',
  success: '復帰しました',
  'error-no-code': '参加コードが含まれていません',
  'error-not-found': '参加コードが見つかりませんでした',
  'error-timeout': '通信がタイムアウトしました',
  'error-auth': '認証に失敗しました',
  'error-admin': '管理者アカウントは復帰の対象外です',
};

const AUTH_TIMEOUT_MS = 12_000;

export function RecoveryAutoPage() {
  const navigate = useNavigate();
  const { urlToken } = useParams<{ urlToken: string }>();
  const [params] = useSearchParams();
  const authState = useAnonymousAuth();
  const {
    state: recoverState,
    call: callRecover,
    confirm: confirmRecover,
    reset,
  } = useRecoverParticipant();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>('auth-pending');
  const [detail, setDetail] = useState<string | null>(null);
  const [fallbackOpen, setFallbackOpen] = useState(false);

  // 1. URL query → localStorage の順で参加コード解決
  // (urlToken が districtId と必ずしも一致しないが、ローカル保存 key は urlToken ベースで十分:
  //  RecoveryCodeModal の persistDistrictId に urlToken or districtId を渡す呼出側の責務)
  const codeFromQuery = params.get('c') ?? '';
  const persistedCode = urlToken ? loadRecoveryCode(urlToken) : null;
  const effectiveCode = codeFromQuery || persistedCode || '';

  // 2. 認証タイムアウト監視 (旧 L24-35)
  useEffect(() => {
    if (authState.status !== 'loading') return;
    const t = setTimeout(() => {
      setStep('error-auth');
      setDetail(
        'ブラウザの「サイトデータの保存」が許可されているかご確認ください。プライベートモードや一部の制限ブラウザでは動作しないことがあります。',
      );
    }, AUTH_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [authState.status]);

  // 3. auth 完了後、effectiveCode があれば自動 call
  useEffect(() => {
    if (authState.status !== 'authenticated') return;
    if (!urlToken) {
      setStep('error-no-code');
      setDetail('地区 URL トークンが指定されていません。');
      return;
    }
    if (!effectiveCode) {
      setStep('error-no-code');
      setDetail('参加コードが含まれていません。手動入力で復帰できます。');
      setFallbackOpen(true);
      return;
    }
    setStep('recovering');
    // 解釈B: urlToken は必須 (callable 側で地区を解決し、その 1 地区のみ復元する)。
    // urlToken 欠如時は上の早期 return で弾いているため、ここでは常に有効。
    void callRecover({ urlToken, recoveryCode: effectiveCode });
    // 一度しか call しない (effectiveCode が固定の限り)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.status, urlToken, effectiveCode]);

  // 4. recoverState 監視: 成功で navigate replace、失敗で fallback modal を開く
  useEffect(() => {
    if (recoverState.status === 'success') {
      setStep('success');
      if (urlToken && effectiveCode) {
        // 成功 code を localStorage に保存 (deep link 再訪時の oneshot 自動復帰の安全網)。
        persistRecoveryCode(urlToken, effectiveCode);
      }
      // 解釈B: 復帰は常に 1 地区のみ。accessibleDistrictCount(合計アクセス地区数)を「復帰地区数」と
      // 誤認させないため、地区数を出さない文言にする。
      if (recoverState.data.noop) {
        // 同一 uid (自分の QR を自分の端末で読んだ等): 変更なし。
        toast('info', 'この端末はすでにこの参加情報を使っています');
      } else {
        toast('success', 'この地区の参加状態を復帰しました');
      }
      const t = setTimeout(() => {
        navigate(`/m/${urlToken}/map`, { replace: true });
      }, 600);
      return () => clearTimeout(t);
    }
    if (recoverState.status === 'needs-confirm') {
      // R-70 P6: この端末に別人の参加情報がある。自動では置き換えず確認を求める。
      setStep('needs-confirm');
      setDetail(null);
      return undefined;
    }
    if (recoverState.status === 'error') {
      const msg = recoverState.error.message;
      const code = (recoverState.error as Error & { code?: string }).code ?? '';
      if (msg.includes('1 分後に再試行')) {
        setStep('error-timeout');
        setDetail(msg);
      } else if (code.includes('permission-denied')) {
        // 管理者アカウントのコード (2026-10-22 H1)。何度やっても通らないので保存済みコードも消す。
        setStep('error-admin');
        setDetail(msg);
        if (urlToken) clearRecoveryCode(urlToken);
      } else if (msg.includes('見つかりません') || msg.includes('not-found')) {
        setStep('error-not-found');
        setDetail(msg);
        // 自動復帰失敗 → localStorage の古いコードを除去 (再ループ防止)
        if (urlToken) clearRecoveryCode(urlToken);
      } else {
        setStep('error-not-found');
        setDetail(msg);
      }
      setFallbackOpen(true);
    }
    // Sprint BG-DataSettings 9: TS7030 解消 — success 経路以外でも明示的に undefined return
    // (success 経路は早期 return ですでに cleanup を返却済)。
    return undefined;
  }, [recoverState, navigate, toast, urlToken, effectiveCode]);

  const handleManualRetry = useCallback(() => {
    reset();
    setFallbackOpen(true);
  }, [reset]);

  const handleBack = useCallback(() => {
    if (urlToken) navigate(`/m/${urlToken}`);
    else navigate('/');
  }, [navigate, urlToken]);

  // R-70 P6: 上書きを了承 → confirmOverwrite:true で再送 (step は進行中表示に戻す)。
  const handleConfirmOverwrite = useCallback(() => {
    setStep('recovering');
    void confirmRecover();
  }, [confirmRecover]);

  const isError =
    step === 'error-no-code' ||
    step === 'error-not-found' ||
    step === 'error-timeout' ||
    step === 'error-auth' ||
    step === 'error-admin';
  const isInProgress = step === 'auth-pending' || step === 'recovering';

  return (
    <main className="mx-auto max-w-md px-6 py-12 text-center">
      <h1 className="text-2xl font-bold mb-6">🔑 自動復帰</h1>

      {isInProgress && (
        <Card padding="lg">
          <div
            className="mx-auto w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"
            role="status"
            aria-label="処理中"
          />
          <p className="text-slate-700 text-sm" data-testid="recovery-auto-step">
            {STEP_LABEL[step]}
          </p>
          <p className="text-xs text-slate-400 mt-3">
            時間がかかる場合は、ブラウザのページを再読み込みしてください。
          </p>
        </Card>
      )}

      {step === 'success' && (
        <Card padding="lg" className="border-success-600">
          <p className="text-success-700 text-base font-medium" data-testid="recovery-auto-step">
            ✓ 復帰しました。地図ページへ移動します…
          </p>
        </Card>
      )}

      {step === 'needs-confirm' && recoverState.status === 'needs-confirm' && (
        <Card padding="lg" className="border-warning-300 text-left">
          <p className="sr-only" data-testid="recovery-auto-step">
            {STEP_LABEL[step]}
          </p>
          <RecoveryOverwriteConfirm
            existingNickname={recoverState.existingNickname}
            incomingNickname={recoverState.incomingNickname}
            onConfirm={handleConfirmOverwrite}
            onCancel={handleBack}
          />
        </Card>
      )}

      {isError && (
        <Card padding="lg" className="border-danger-600 text-left">
          <p
            className="text-danger-700 text-base font-medium mb-2"
            data-testid="recovery-auto-step"
          >
            {STEP_LABEL[step]}
          </p>
          {detail && <p className="text-xs text-slate-500 break-all mb-4">{detail}</p>}
          <div className="grid gap-2">
            <Button variant="primary" size="md" onClick={handleManualRetry}>
              コードを手入力する
            </Button>
            <Button variant="secondary" size="md" onClick={handleBack}>
              最初に戻る
            </Button>
          </div>
        </Card>
      )}

      <RecoveryRecoverModal
        open={fallbackOpen}
        districtToken={urlToken}
        onClose={() => setFallbackOpen(false)}
        onRecovered={() => {
          setFallbackOpen(false);
          if (urlToken) {
            navigate(`/m/${urlToken}/map`, { replace: true });
          }
        }}
      />
    </main>
  );
}
